import React, { useState, useEffect, useRef, useTransition } from "react";
import { Tabs, Tab, Box, Card } from "@mui/material";
import MDBox from "components/MDBox";
import LoadingScreen from "layouts/loading/loadingscreen";

// 탭용 서브 컴포넌트 import
import HygieneSheetTab from "./Operate/HygieneSheetTab";
import PropertySheetTab from "./Operate/PropertySheetTab";
import RetailBusinessTab from "./Operate/RetailBusinessTab";
import SubRestaurantTab from "./Operate/SubRestaurantTab";
import HandOverSheetTab from "./Operate/HandoverSheetTab";
import AccountMembersFilesTab from "./Operate/AccountMembersFilesTab";
import HeaderWithLogout from "components/Common/HeaderWithLogout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

// 🔹 로그인 유저의 부서/직책 코드 가져오기 (localStorage 기준)
const getUserCodes = () => {
  const dept = localStorage.getItem("department"); // ex) "2"
  const pos = localStorage.getItem("position");    // ex) "4"

  return {
    deptCode: dept != null ? Number(dept) : null,
    posCode: pos != null ? Number(pos) : null,
  };
};

// 🔹 route와 동일한 방식의 접근 권한 체크 함수
const hasAccess = (tab, deptCode, posCode) => {
  const { allowedDepartments, allowedPositions, accessMode = "AND" } = tab;

  const hasDeptCond =
    Array.isArray(allowedDepartments) && allowedDepartments.length > 0;
  const hasPosCond =
    Array.isArray(allowedPositions) && allowedPositions.length > 0;

  // 조건이 하나도 없으면 모두 접근 허용
  if (!hasDeptCond && !hasPosCond) return true;

  const deptOk =
    hasDeptCond && deptCode != null
      ? allowedDepartments.includes(deptCode)
      : false;
  const posOk =
    hasPosCond && posCode != null
      ? allowedPositions.includes(posCode)
      : false;

  if (accessMode === "OR") {
    if (hasDeptCond && hasPosCond) return deptOk || posOk;
    if (hasDeptCond) return deptOk; // 부서만 있을 때
    if (hasPosCond) return posOk;   // 직책만 있을 때
    return true;
  } else {
    // AND: 없는 조건은 true 로 간주 (부서만 있으면 부서만 체크)
    const finalDeptOk = hasDeptCond ? deptOk : true;
    const finalPosOk = hasPosCond ? posOk : true;
    return finalDeptOk && finalPosOk;
  }
};

function OperateTabs() {
  const [tabIndex, setTabIndex] = useState(0);
  const [contentTabIndex, setContentTabIndex] = useState(0);
  const [tabSwitchLoading, setTabSwitchLoading] = useState(false);
  const [, startTransition] = useTransition();
  const switchTimerRef = useRef(null);
  const TAB_SWITCH_DELAY_MS = 320;
  const { deptCode, posCode } = getUserCodes();

  const handleTabChange = (_, newValue) => {
    if (newValue === tabIndex) return;

    setTabIndex(newValue);
    setTabSwitchLoading(true);

    if (switchTimerRef.current) clearTimeout(switchTimerRef.current);
    switchTimerRef.current = setTimeout(() => {
      startTransition(() => {
        setContentTabIndex(newValue);
      });
    }, TAB_SWITCH_DELAY_MS);
  };

  // ✅ 숫자 이모지 아이콘
  const numberIcons = ["1️⃣", "2️⃣", "3️⃣", "4️⃣", "5️⃣", "6️⃣", "7️⃣", "8️⃣", "9️⃣", "🔟"];

  // 직책 -> (0: 대표, 1:팀장, 2:파트장, 3:매니저)
  // 부서 -> (0:대표, 1: 신사업팀, 2: 회계팀, 3: 인사팀, 4: 영업팀, 5: 운영팀,  6: 개발팀, 7:현장, 8: 급식사업부, 9:기획팀)

  // 🔹 탭 설정 + 권한 정의
  const tabConfig = [
    {
      key: "hygiene",
      label: "🧹 위생관리",
      iconIndex: 0,
      component: <HygieneSheetTab />,
      allowedDepartments: [0, 2, 3, 4, 5, 6, 7, 8, 9],   // 🔹 부서권한
      allowedPositions: [0, 1, 2, 3, 4, 5, 6, 7,],   // 🔹 직책권한
      accessMode: "AND",
    },
    {
      key: "property",
      label: "📦 기물관리",
      iconIndex: 1,
      component: <PropertySheetTab />,
      allowedDepartments: [0, 2, 3, 4, 5, 6, 7, 8, 9],   // 🔹 부서권한
      allowedPositions: [0, 1, 2, 3, 4, 5, 6, 7,],   // 🔹 직책권한
      accessMode: "AND",
    },
    {
      key: "retail",
      label: "🏢 거래처관리",
      iconIndex: 2,
      component: <RetailBusinessTab />,
      allowedDepartments: [0, 2, 3, 4, 5, 6, 8, 9],   // 🔹 부서권한
      allowedPositions: [0, 1, 2, 3, 4, 5, 6, 7,],   // 🔹 직책권한
      accessMode: "AND",
    },
    {
      key: "subRestaurant",
      label: "🏢 대체업체관리",
      iconIndex: 3,
      component: <SubRestaurantTab />,
      allowedDepartments: [0, 2, 3, 4, 5, 6, 8, 9],   // 🔹 부서권한
      allowedPositions: [0, 1, 2, 3, 4, 5, 6, 7,],   // 🔹 직책권한
      accessMode: "AND",
    },
    {
      key: "handover",
      label: "🔁 인수인계 관리",
      iconIndex: 4,
      component: <HandOverSheetTab />,
      allowedDepartments: [0, 2, 3, 4, 5, 6, 7, 8, 9],   // 🔹 부서권한
      allowedPositions: [0, 1, 2, 3, 4, 5, 6, 7,],   // 🔹 직책권한
      accessMode: "AND",
    },
    {
      key: "accountFiles",
      label: "📋 자격/보건 서류 관리",
      iconIndex: 5,
      component: <AccountMembersFilesTab />,
      allowedDepartments: [0, 2, 3, 4, 5, 6],   // 🔹 부서권한
      allowedPositions: [0, 1, 2, 3, 4, 5, 6, 7,],   // 🔹 직책권한
      accessMode: "AND",
    },
  ];

  // 🔹 현재 유저 기준으로 보여줄 탭만 필터링
  const visibleTabs = tabConfig.filter((tab) => hasAccess(tab, deptCode, posCode));

  // 🔹 권한 변경/로그인 변경 등으로 visibleTabs 길이가 줄었을 때 index 보정
  useEffect(() => {
    if (tabIndex >= visibleTabs.length) {
      setTabIndex(0);
    }
    if (contentTabIndex >= visibleTabs.length) {
      setContentTabIndex(0);
    }
  }, [visibleTabs.length, tabIndex, contentTabIndex]);

  useEffect(
    () => () => {
      if (switchTimerRef.current) clearTimeout(switchTimerRef.current);
    },
    []
  );

  useEffect(() => {
    if (contentTabIndex === tabIndex && tabSwitchLoading) {
      setTabSwitchLoading(false);
    }
  }, [contentTabIndex, tabIndex, tabSwitchLoading]);

  // 🔹 권한 있는 탭이 하나도 없을 때
  if (visibleTabs.length === 0) {
    return (
      <Card sx={{ borderRadius: "16px", padding: 3 }}>
        <MDBox textAlign="center">조회 가능한 운영 탭이 없습니다. (권한 확인 필요)</MDBox>
      </Card>
    );
  }
  const activeTabComponent = visibleTabs[contentTabIndex]?.component ?? visibleTabs[tabIndex]?.component;

  return (
    <Card sx={{ borderRadius: "16px", boxShadow: "0px 5px 15px rgba(0,0,0,0.1)" }}>
      <MDBox
        sx={{
          position: "sticky",
          top: 0,             // 상단 고정 위치 (필요하면 56, 64 등으로 조절 가능)
          zIndex: 10,
          backgroundColor: "#ffffff",
          borderBottom: "1px solid #eee",
        }}
      >
        {/* 🔹 공통 헤더 사용 */}
        {/* <HeaderWithLogout showMenuButton title="📁고객사 관리" /> */}
        <DashboardNavbar title="📁고객사 관리" />
        {/* 탭 상단 */}
        <Tabs
          value={tabIndex}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            backgroundColor: "#f7f7f7",
            borderRadius: "16px 16px 0 0",
            "& .MuiTabs-indicator": {
              backgroundColor: "#ff9800",
              height: "3px",
              borderRadius: "3px",
            },
          }}
        >
          {visibleTabs.map((tab, index) => (
            <Tab
              key={tab.key}
              label={
                <Box display="flex" alignItems="center" gap={1}>
                  {/* <span>{numberIcons[tab.iconIndex]}</span> */}
                  <span>{tab.label}</span>
                </Box>
              }
              sx={{
                fontSize: "0.8rem",
                minWidth: 120,
                textTransform: "none",
                color: tabIndex === index ? "#ff9800" : "#666",
                fontWeight: "bold",
                transition: "0.2s",
                "&:hover": {
                  color: "#ff9800",
                  opacity: 0.8,
                },
              }}
            />
          ))}
        </Tabs>
      </MDBox>
      {/* 탭 내용 */}
      <MDBox p={2} sx={{ position: "relative" }}>
        {activeTabComponent}
        {tabSwitchLoading && (
          <MDBox
            sx={{
              position: "absolute",
              inset: 0,
              zIndex: 20,
              "& .loading-container": {
                height: "100%",
              },
            }}
          >
            <LoadingScreen />
          </MDBox>
        )}
      </MDBox>
    </Card>
  );
}

export default OperateTabs;
